import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";
import { movies } from "@/mocks/movies";
import { useSeo } from "@/hooks/useSeo";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") || "";
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return [];
    return movies.filter(
      (m) =>
        m.title.toLowerCase().includes(q) ||
        m.category.toLowerCase().includes(q) ||
        m.actors.some((a) => a.toLowerCase().includes(q)) ||
        m.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }, [keyword]);

  useSeo({
    title: keyword ? `搜索「${keyword}」 - 恋爱的味道` : "搜索影视作品 - 恋爱的味道",
    description: keyword
      ? `在恋爱的味道搜索「${keyword}」的结果，共找到 ${results.length} 部相关影视作品，涵盖爱情电视剧与电影。`
      : "在恋爱的味道搜索您喜爱的爱情电视剧与情感电影，快速找到心仪的影视作品与剧情介绍。",
    keywords: `搜索,${keyword || "爱情影视"},影视作品,恋爱的味道`,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    setParams(value ? { q: value } : {});
  };

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8">
        <nav className="flex flex-wrap items-center gap-1 text-sm text-foreground-600">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">搜索</span>
        </nav>

        <div className="mt-6 rounded-lg border border-background-200 bg-white p-5 md:p-6">
          <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {keyword ? (
              <>
                搜索「<span className="text-primary-600">{keyword}</span>」
              </>
            ) : (
              "搜索影视作品"
            )}
          </h1>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row">
            <div className="flex h-11 flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                type="text"
                placeholder="输入作品名称、类型或演员关键词…"
                className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
              />
            </div>
            <button
              type="submit"
              className="h-11 rounded-full bg-primary-500 px-8 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              搜索
            </button>
          </form>
          {keyword && (
            <p className="mt-3 text-sm text-foreground-600">
              找到 <strong className="font-semibold text-primary-600">{results.length}</strong> 部相关影视作品
            </p>
          )}
        </div>

        {keyword && results.length > 0 && (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {results.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        )}

        {keyword && results.length === 0 && (
          <div className="mt-8">
            <EmptyState
              title="没有找到相关影视作品"
              desc={`没有找到与「${keyword}」匹配的影视作品，换个关键词试试，或返回首页浏览热门推荐。`}
            />
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <Link
                to="/"
                className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                返回首页
              </Link>
              <Link
                to="/category/电视剧"
                className="rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100 whitespace-nowrap"
              >
                浏览电视剧
              </Link>
              <Link
                to="/category/电影"
                className="rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100 whitespace-nowrap"
              >
                浏览电影
              </Link>
            </div>
          </div>
        )}

        {!keyword && (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-12 text-center">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">输入关键词开始搜索</h3>
            <p className="mt-2 text-sm text-foreground-600">
              支持作品名称、类型标签与演员姓名的模糊匹配，试试搜索「都市」「青春」或「情书」。
            </p>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}